/**
 * File Management Store
 *
 * Zustand store for managing file operations, recent files, auto-save,
 * and unsaved changes warnings.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  StoredModel,
  RecentFile,
  ExportOptions,
  ImportResult,
  AutoSaveStatus,
  LibraryWeatherFile,
} from './types';

/** Notification for model load/create events */
export interface FileNotification {
  id: string;
  type: 'success' | 'info';
  message: string;
}
import { DEFAULT_EXPORT_OPTIONS } from './types';
import { modelStorage, createStoredModel, generateModelId } from './modelStorage';
import { importIDFFile, exportIDF, downloadFile, sanitizeFilename } from './fileOperations';
import { useIDFStore } from '../stores/idfStore';

const MAX_RECENT_FILES = 10;
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

// Lock to prevent concurrent save operations
let saveInProgress = false;

interface FileState {
  /** Currently open model ID (null for new/unsaved) */
  currentModelId: string | null;

  /** Current file name */
  currentFileName: string | null;

  /** Recent files list */
  recentFiles: RecentFile[];

  /** Auto-save status */
  autoSave: AutoSaveStatus;

  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;

  /** Last saved content hash for change detection */
  lastSavedHash: string | null;

  /** Export options (persisted) */
  exportOptions: ExportOptions;

  /** Selected weather file ID (for bundled files) */
  selectedWeatherFileId: string | null;

  /** Selected weather file from library (for library files) */
  selectedLibraryWeatherFile: LibraryWeatherFile | null;

  /** Import dialog state */
  importDialogOpen: boolean;
  importResult: ImportResult | null;

  /** Export dialog state */
  exportDialogOpen: boolean;

  /** Saved models dialog state */
  savedModelsDialogOpen: boolean;

  /** Example models dialog state */
  exampleModelsDialogOpen: boolean;

  /** List of saved models */
  savedModels: StoredModel[];

  /** Loading states */
  isLoading: boolean;
  isSaving: boolean;

  /** Notification for model load/create feedback */
  notification: FileNotification | null;

  /** Signal to navigate to editor (used when loading from home page) */
  shouldNavigateToEditor: boolean;

  // Actions
  /** Import a file */
  importFile: (file: File) => Promise<ImportResult>;

  /** Confirm and load an import */
  confirmImport: (content: string, filename: string) => void;

  /** Export current document */
  exportDocument: (filename?: string) => void;

  /** Save current document to local storage */
  saveModel: (name?: string) => Promise<void>;

  /** Load a model from storage */
  loadModel: (id: string) => Promise<void>;

  /** Load an example model */
  loadExampleModel: (idfUrl: string, name: string) => Promise<void>;

  /** Delete a saved model */
  deleteModel: (id: string) => Promise<void>;

  /** Refresh the saved models list */
  refreshSavedModels: () => Promise<void>;

  /** Create a new document */
  newDocument: () => void;

  /** Add to recent files */
  addToRecentFiles: (file: RecentFile) => void;

  /** Clear recent files */
  clearRecentFiles: () => void;

  /** Set export options */
  setExportOptions: (options: Partial<ExportOptions>) => void;

  /** Set selected weather file (bundled) */
  setSelectedWeatherFile: (id: string | null) => void;

  /** Set selected library weather file */
  setSelectedLibraryWeatherFile: (file: LibraryWeatherFile | null) => void;

  /** Toggle dialogs */
  setImportDialogOpen: (open: boolean) => void;
  setExportDialogOpen: (open: boolean) => void;
  setSavedModelsDialogOpen: (open: boolean) => void;
  setExampleModelsDialogOpen: (open: boolean) => void;

  /** Mark as dirty (unsaved changes) */
  markDirty: () => void;

  /** Mark as clean (saved) */
  markClean: () => void;

  /** Check if there are unsaved changes (for navigation guards) */
  checkUnsavedChanges: () => boolean;

  /** Enable/disable auto-save */
  setAutoSaveEnabled: (enabled: boolean) => void;

  /** Trigger auto-save if needed */
  triggerAutoSave: () => Promise<void>;

  /** Show a notification */
  showNotification: (type: FileNotification['type'], message: string) => void;

  /** Clear notification */
  clearNotification: () => void;

  /** Clear navigation signal (called after navigation is performed) */
  clearNavigationSignal: () => void;

  /** Set the current file name */
  setCurrentFileName: (name: string) => void;
}

/**
 * Simple hash function for change detection.
 */
function hashContent(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const chr = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString(36);
}

export const useFileStore = create<FileState>()(
  devtools(
    persist(
      (set, get) => ({
        currentModelId: null,
        currentFileName: null,
        recentFiles: [],
        autoSave: {
          enabled: true,
          lastSaved: null,
          isDirty: false,
          modelId: null,
        },
        hasUnsavedChanges: false,
        lastSavedHash: null,
        exportOptions: DEFAULT_EXPORT_OPTIONS,
        selectedWeatherFileId: 'denver',
        selectedLibraryWeatherFile: null,
        importDialogOpen: false,
        importResult: null,
        exportDialogOpen: false,
        savedModelsDialogOpen: false,
        exampleModelsDialogOpen: false,
        savedModels: [],
        isLoading: false,
        isSaving: false,
        notification: null,
        shouldNavigateToEditor: false,

        importFile: async (file: File) => {
          set({ isLoading: true });
          try {
            const result = await importIDFFile(file);
            set({ importResult: result, importDialogOpen: true });
            return result;
          } finally {
            set({ isLoading: false });
          }
        },

        confirmImport: (content: string, filename: string) => {
          const idfStore = useIDFStore.getState();
          idfStore.parseContent(content);

          const recentFile: RecentFile = {
            id: generateModelId(),
            name: filename,
            lastOpened: new Date(),
            source: 'imported',
          };

          set({
            currentModelId: null, // Not saved yet
            currentFileName: filename,
            importDialogOpen: false,
            importResult: null,
            hasUnsavedChanges: true,
            lastSavedHash: null,
          });

          get().addToRecentFiles(recentFile);
          get().showNotification('success', `Imported: ${filename}`);

          // Signal navigation if on home page
          if (window.location.pathname === '/') {
            set({ shouldNavigateToEditor: true });
          }
        },

        exportDocument: (filename?: string) => {
          const idfStore = useIDFStore.getState();
          const { document } = idfStore;
          if (!document) return;

          const { exportOptions, currentFileName } = get();
          const content = exportIDF(document, exportOptions);

          const ext = exportOptions.format === 'imf' ? '.imf' : '.idf';
          const name = filename ?? currentFileName ?? 'model';
          const safeName = sanitizeFilename(name.replace(/\.(idf|imf)$/i, ''));

          downloadFile(content, `${safeName}${ext}`);
          set({ exportDialogOpen: false });
        },

        saveModel: async (name?: string) => {
          const idfStore = useIDFStore.getState();
          const { document } = idfStore;
          if (!document) return;

          // Prevent concurrent saves using module-level lock
          if (saveInProgress) return;
          saveInProgress = true;

          set({ isSaving: true });

          try {
            const content = idfStore.serialize();
            const { currentModelId, currentFileName } = get();

            const modelName = name ?? currentFileName ?? 'Untitled Model';
            const id = currentModelId ?? generateModelId();

            // Create or update the stored model
            const existingModel = currentModelId
              ? await modelStorage.loadModel(currentModelId)
              : null;

            const storedModel = createStoredModel(
              content,
              modelName,
              id,
              existingModel?.thumbnail
            );

            if (existingModel) {
              storedModel.createdAt = existingModel.createdAt;
            }

            await modelStorage.saveModel(storedModel);

            // Update state
            const contentHash = hashContent(content);
            set({
              currentModelId: id,
              currentFileName: modelName,
              hasUnsavedChanges: false,
              lastSavedHash: contentHash,
              autoSave: {
                ...get().autoSave,
                lastSaved: new Date(),
                isDirty: false,
                modelId: id,
              },
            });

            // Mark IDF store as clean
            idfStore.markClean();

            // Add to recent files
            get().addToRecentFiles({
              id,
              name: modelName,
              lastOpened: new Date(),
              source: 'local',
              thumbnail: storedModel.thumbnail,
            });

            // Refresh saved models list
            await get().refreshSavedModels();
          } finally {
            saveInProgress = false;
            set({ isSaving: false });
          }
        },

        loadModel: async (id: string) => {
          set({ isLoading: true });

          try {
            const model = await modelStorage.loadModel(id);
            if (!model) {
              throw new Error('Model not found');
            }

            const idfStore = useIDFStore.getState();
            idfStore.parseContent(model.content);

            const contentHash = hashContent(model.content);
            set({
              currentModelId: model.id,
              currentFileName: model.name,
              hasUnsavedChanges: false,
              lastSavedHash: contentHash,
              savedModelsDialogOpen: false,
            });

            // Add to recent files
            get().addToRecentFiles({
              id: model.id,
              name: model.name,
              lastOpened: new Date(),
              source: 'local',
              thumbnail: model.thumbnail,
            });

            // Mark IDF store as clean
            idfStore.markClean();

            // Show notification
            get().showNotification('success', `Loaded: ${model.name}`);

            // Signal navigation if on home page
            if (window.location.pathname === '/') {
              set({ shouldNavigateToEditor: true });
            }
          } finally {
            set({ isLoading: false });
          }
        },

        loadExampleModel: async (idfUrl: string, name: string) => {
          set({ isLoading: true });

          try {
            // Fetch the example IDF file
            const response = await fetch(idfUrl);
            if (!response.ok) {
              throw new Error(`Failed to load example: ${response.statusText}`);
            }

            const content = await response.text();

            // Validate that we received IDF content, not HTML (e.g., from SPA fallback)
            const trimmedContent = content.trim();
            if (
              trimmedContent.startsWith('<!DOCTYPE') ||
              trimmedContent.startsWith('<html') ||
              trimmedContent.startsWith('<!doctype')
            ) {
              throw new Error(
                `Example file not found: ${idfUrl}. The server returned an HTML page instead of an IDF file.`
              );
            }

            const idfStore = useIDFStore.getState();
            idfStore.parseContent(content);

            set({
              currentModelId: null, // Example models are not saved until user saves
              currentFileName: name,
              hasUnsavedChanges: true, // Treat as unsaved so user can modify
              lastSavedHash: null,
              exampleModelsDialogOpen: false,
            });

            // Add to recent files
            get().addToRecentFiles({
              id: generateModelId(),
              name,
              lastOpened: new Date(),
              source: 'imported',
            });

            // Show notification
            get().showNotification('success', `Loaded example: ${name}`);

            // Signal navigation if on home page
            if (window.location.pathname === '/') {
              set({ shouldNavigateToEditor: true });
            }
          } finally {
            set({ isLoading: false });
          }
        },

        deleteModel: async (id: string) => {
          await modelStorage.deleteModel(id);

          // Remove from recent files
          const { recentFiles, currentModelId } = get();
          set({
            recentFiles: recentFiles.filter((f) => f.id !== id),
          });

          // If this was the current model, clear it
          if (currentModelId === id) {
            set({
              currentModelId: null,
              currentFileName: null,
            });
          }

          // Refresh saved models list
          await get().refreshSavedModels();
        },

        refreshSavedModels: async () => {
          const models = await modelStorage.listModels();
          set({ savedModels: models });
        },

        newDocument: () => {
          const idfStore = useIDFStore.getState();
          idfStore.clear();

          // Set up a minimal new document
          idfStore.parseContent(`!-Generator Envelop
!-Option SortedOrder

Version,
  24.1;                    !- Version Identifier

SimulationControl,
  No,                      !- Do Zone Sizing Calculation
  No,                      !- Do System Sizing Calculation
  No,                      !- Do Plant Sizing Calculation
  No,                      !- Run Simulation for Sizing Periods
  Yes;                     !- Run Simulation for Weather File Run Periods

Building,
  New Building,            !- Name
  0,                       !- North Axis {deg}
  Suburbs,                 !- Terrain
  0.04,                    !- Loads Convergence Tolerance Value {W}
  0.4,                     !- Temperature Convergence Tolerance Value {deltaC}
  FullInteriorAndExterior, !- Solar Distribution
  25,                      !- Maximum Number of Warmup Days
  6;                       !- Minimum Number of Warmup Days
`);

          set({
            currentModelId: null,
            currentFileName: 'Untitled.idf',
            hasUnsavedChanges: false,
            lastSavedHash: null,
          });

          get().showNotification('info', 'New model created');

          // Signal navigation if on home page
          if (window.location.pathname === '/') {
            set({ shouldNavigateToEditor: true });
          }
        },

        addToRecentFiles: (file: RecentFile) => {
          const { recentFiles } = get();

          // Remove if already exists
          const filtered = recentFiles.filter((f) => f.id !== file.id);

          // Add to beginning
          const updated = [file, ...filtered].slice(0, MAX_RECENT_FILES);

          set({ recentFiles: updated });
        },

        clearRecentFiles: () => {
          set({ recentFiles: [] });
        },

        setExportOptions: (options: Partial<ExportOptions>) => {
          set((state) => ({
            exportOptions: { ...state.exportOptions, ...options },
          }));
        },

        setSelectedWeatherFile: (id: string | null) => {
          set({ selectedWeatherFileId: id });
        },

        setSelectedLibraryWeatherFile: (file: LibraryWeatherFile | null) => {
          set({ selectedLibraryWeatherFile: file });
        },

        setImportDialogOpen: (open: boolean) => {
          set({ importDialogOpen: open, importResult: open ? get().importResult : null });
        },

        setExportDialogOpen: (open: boolean) => {
          set({ exportDialogOpen: open });
        },

        setSavedModelsDialogOpen: (open: boolean) => {
          if (open) {
            get().refreshSavedModels().catch((error: unknown) => {
              console.error('Failed to refresh saved models:', error);
            });
          }
          set({ savedModelsDialogOpen: open });
        },

        setExampleModelsDialogOpen: (open: boolean) => {
          set({ exampleModelsDialogOpen: open });
        },

        markDirty: () => {
          set({
            hasUnsavedChanges: true,
            autoSave: {
              ...get().autoSave,
              isDirty: true,
            },
          });
        },

        markClean: () => {
          const idfStore = useIDFStore.getState();
          const content = idfStore.serialize();
          const contentHash = hashContent(content);

          set({
            hasUnsavedChanges: false,
            lastSavedHash: contentHash,
            autoSave: {
              ...get().autoSave,
              isDirty: false,
              lastSaved: new Date(),
            },
          });
        },

        checkUnsavedChanges: () => {
          const { hasUnsavedChanges, lastSavedHash } = get();
          const idfStore = useIDFStore.getState();

          // If explicitly marked as having unsaved changes
          if (hasUnsavedChanges) return true;

          // Check IDF store dirty flag
          if (idfStore.isDirty) return true;

          // If we have a saved hash, compare current content
          if (lastSavedHash) {
            const currentContent = idfStore.serialize();
            const currentHash = hashContent(currentContent);
            return currentHash !== lastSavedHash;
          }

          // New document with content = unsaved changes
          return idfStore.document !== null && idfStore.document.objects.length > 0;
        },

        setAutoSaveEnabled: (enabled: boolean) => {
          set({
            autoSave: {
              ...get().autoSave,
              enabled,
            },
          });
        },

        triggerAutoSave: async () => {
          const { autoSave, currentModelId } = get();

          // Only auto-save if enabled and we have a saved model
          if (!autoSave.enabled || !currentModelId) return;

          // Check if there are actual changes
          if (!get().checkUnsavedChanges()) return;

          // Perform the save with error handling
          try {
            await get().saveModel();
          } catch (error) {
            console.error('Auto-save failed:', error);
          }
        },

        showNotification: (type: FileNotification['type'], message: string) => {
          const id = `notif-${Date.now()}`;
          set({ notification: { id, type, message } });

          // Auto-clear after 4 seconds
          setTimeout(() => {
            const { notification } = get();
            if (notification?.id === id) {
              set({ notification: null });
            }
          }, 4000);
        },

        clearNotification: () => {
          set({ notification: null });
        },

        clearNavigationSignal: () => {
          set({ shouldNavigateToEditor: false });
        },

        setCurrentFileName: (name: string) => {
          // Normalize: remove .idf/.imf extension if present
          const normalizedName = name.replace(/\.(idf|imf)$/i, '');
          set({
            currentFileName: normalizedName,
            hasUnsavedChanges: true,
          });
        },
      }),
      {
        name: 'envelop-file-store',
        partialize: (state) => ({
          recentFiles: state.recentFiles,
          exportOptions: state.exportOptions,
          selectedWeatherFileId: state.selectedWeatherFileId,
          selectedLibraryWeatherFile: state.selectedLibraryWeatherFile,
          autoSave: { enabled: state.autoSave.enabled },
        }),
      }
    ),
    { name: 'FileStore' }
  )
);

// Auto-save interval
let autoSaveIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Start the auto-save interval.
 */
export function startAutoSave(): void {
  if (autoSaveIntervalId) return;

  autoSaveIntervalId = setInterval(() => {
    const store = useFileStore.getState();
    if (store.autoSave.enabled && store.currentModelId) {
      void store.triggerAutoSave();
    }
  }, AUTO_SAVE_INTERVAL);
}

/**
 * Stop the auto-save interval.
 */
export function stopAutoSave(): void {
  if (autoSaveIntervalId) {
    clearInterval(autoSaveIntervalId);
    autoSaveIntervalId = null;
  }
}

/**
 * Set up beforeunload handler for unsaved changes warning.
 */
export function setupUnsavedChangesWarning(): () => void {
  const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
    const store = useFileStore.getState();
    if (store.checkUnsavedChanges()) {
      e.preventDefault();
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}
